// D3D-774 v1.2: 仮設道路AI（点群のみ対応・表示中データ利用）の client 側の型・定数の単一定義。

/** 目的キー。表示ラベルは roadAiDiagnostics.js の OBJECTIVE_LABELS が単一の正とする。 */
export const ROAD_AI_OBJECTIVES = [
	"cut",
	"fill",
	"over_cut",
	"over_fill",
	"cf_diff",
] as const;
export type RoadAiObjective = (typeof ROAD_AI_OBJECTIVES)[number];

/** 設計面が無いと意味が定まらない指標（AC-7 / AC-23）。 */
export const ROAD_AI_DESIGN_DEPENDENT_METRICS = [
	"over_cut",
	"over_fill",
] as const;
export type RoadAiDesignDependentMetric =
	(typeof ROAD_AI_DESIGN_DEPENDENT_METRICS)[number];

/** 点群のみモードで設計依存指標に表示する文言（AC-7）。 */
export const ROAD_AI_METRIC_NOT_APPLICABLE_LABEL = "対象外" as const;

export const ROAD_AI_MODES = ["standard", "point_cloud_only"] as const;
export type RoadAiMode = (typeof ROAD_AI_MODES)[number];

/** モード別の既定目的（AC-26 / v1.0 AC-F17）。 */
export const ROAD_AI_DEFAULT_OBJECTIVES: Readonly<
	Record<RoadAiMode, readonly RoadAiObjective[]>
> = {
	standard: ["cut", "fill", "over_cut", "over_fill", "cf_diff"],
	point_cloud_only: ["cut", "fill", "cf_diff"],
} as const;

/** 点群のみモードで選択できる目的（AC-23）。 */
export const ROAD_AI_POINT_CLOUD_ONLY_OBJECTIVES = [
	"cut",
	"fill",
	"cf_diff",
] as const;

/**
 * モードに応じて実行に使う目的を確定する（AC-23 / AC-26）。
 * 点群のみモードでは選択可能な目的だけを残し（設計依存の over_cut / over_fill が外れる）、
 * 何も残らない場合はモード既定の 3 目的へフォールバックする。
 */
export const filterObjectivesForMode = (
	objectives: readonly RoadAiObjective[],
	mode: RoadAiMode | null,
): RoadAiObjective[] => {
	if (mode !== "point_cloud_only") return [...objectives];
	const allowed = objectives.filter((objective) =>
		(ROAD_AI_POINT_CLOUD_ONLY_OBJECTIVES as readonly string[]).includes(
			objective,
		),
	);
	return allowed.length > 0
		? allowed
		: [...ROAD_AI_DEFAULT_OBJECTIVES.point_cloud_only];
};

/** 実行対象の点群アセット。名前は使用データ確認メッセージに使う（AC-3 / AC-36）。 */
export interface RoadAiPointCloudRef {
	pointCloudId: number; // point_cloud.point_cloud_id（v2-server の点群クエリのキー）
	assetId: string; // tile-asset のアセットID（文字列のまま保持）
	name: string; // point_cloud_name
}

/** 実行対象の設計アセット。標準モードでのみ存在する。 */
export interface RoadAiDesignRef {
	designId: string; // design.design_id
	assetId: string; // tile-asset のアセットID（v2-server の設計クエリのキー）
	name: string; // design_name
}

/** 実行に使用するデータの確定内容。モードで形が変わるため判別可能 union にする。 */
export type RoadAiDataSelection =
	| {
			mode: "standard";
			pointCloud: RoadAiPointCloudRef;
			design: RoadAiDesignRef;
	  }
	| { mode: "point_cloud_only"; pointCloud: RoadAiPointCloudRef };

/** 開始不可の理由（AC-2）。 */
export const ROAD_AI_BLOCKED_REASONS = [
	"POINT_CLOUD_NONE", // 表示中の点群が 0 件
	"POINT_CLOUD_MULTIPLE", // 表示中の点群が 2 件以上
	"DESIGN_MULTIPLE", // 表示中の設計が 2 件以上
] as const;
export type RoadAiBlockedReason = (typeof ROAD_AI_BLOCKED_REASONS)[number];

/** 表示中データ検査の結果。 */
export type RoadAiDataInspection =
	| { status: "ok"; selection: RoadAiDataSelection }
	| { status: "blocked"; reason: RoadAiBlockedReason };

/** 確定土量。点群のみモードでは設計依存指標を null にする（AC-7）。 */
export interface RoadAiMetrics {
	cut: number; // 掘削量 m3（路面帯＋左右法面。AC-38）
	fill: number; // 盛土量 m3（同上）
	over_cut: number | null; // 掘りすぎ量。点群のみモードは null
	over_fill: number | null; // 盛りすぎ量。点群のみモードは null
	cf_diff: number; // 切盛差
}

/** 指標が設計依存かを判定する型ガード。一覧パネルの表示分岐に使う。 */
export const isDesignDependentMetric = (
	key: keyof RoadAiMetrics,
): key is RoadAiDesignDependentMetric =>
	(ROAD_AI_DESIGN_DEPENDENT_METRICS as readonly string[]).includes(key);

/** 制約違反の内訳。 */
export interface RoadAiViolation {
	type: "road_width_m" | "slope_ratio";
	message: string;
	detail: Readonly<Record<string, unknown>>;
}

/** 候補経路 1 本。id は objective ごとの通し番号を含めて一意にする。 */
export interface RoadAiCandidateEntity {
	id: string; // `${objective}#${indexWithinObjective}`
	objective: RoadAiObjective;
	points: readonly number[]; // 中心線のフラット座標列 [x,y,z,...]（point-cloud CRS）
	metricsFinal: RoadAiMetrics;
	nodeCount: number;
	violations: readonly RoadAiViolation[];
	isFallback: boolean; // 制約違反を含む裁量経路か
}

/** 候補経路一覧の正規化状態。ids が表示順（objectives の入力順）を保持する。 */
export interface RoadAiCandidateState {
	ids: string[];
	entities: Record<string, RoadAiCandidateEntity>;
}

/** 一覧パネルの指標セル表示。 */
export type RoadAiMetricDisplay =
	| { kind: "value"; text: string } // 小数第1位に整形した数値
	| { kind: "not_applicable"; text: string } // 点群のみモードの設計依存指標（AC-7）
	| { kind: "unknown"; text: string }; // 値が取得できない場合

export interface RoadAiDiagnostic {
	code: string;
	message: string;
	detail: Readonly<Record<string, unknown>>;
}

/** 実行中止の理由コード（AC-11 / AC-27 / AC-32）。 */
export const ROAD_AI_RUN_ERROR_CODES = [
	"POINT_CLOUD_NOT_FOUND", // 表示中の点群が現場に存在しない
	"HEIGHT_MAP_UNAVAILABLE", // 現況heightmap が未生成・取得失敗
	"DESIGN_NOT_FOUND", // 表示中の設計が現場に存在しない
	"DESIGN_ORG_MESH_NOT_REGISTERED", // design_path_org 未設定（AC-32）
	"DESIGN_MESH_UNAVAILABLE", // 設計メッシュの取得失敗（AC-27）
	"INVALID_OBJECTIVE_FOR_MODE", // 点群のみモードで over_cut / over_fill を指定（v2-server が明示したときのみ）
	"INVALID_REQUEST", // 受付・照会の入力が不正（座標や型の不正・中継点の上限超過・siteId の書式不正）
	"ROUTE_ENGINE_BUSY", // 経路生成の同時実行数が上限に達している（受付拒否）
	"ROUTE_ENGINE_UNAVAILABLE", // 受付時に worker が 1 台も応答しない（受付拒否）
	"JOB_NOT_FOUND", // 照会先のジョブを特定できない（ハンドルの書式不正・未知のハンドル）
	"SOURCE_CACHE_EXPIRED", // 実行開始までにキャッシュの有効期限が切れた（再実行で解消）
	"ROUTE_ENGINE_INTERRUPTED", // 待機中に worker の消失が確定した（ロックを解除して再実行を促す）
	"ROUTE_ENGINE_FAILED", // 経路生成の実行失敗
	"NETWORK_ERROR", // 通信失敗
] as const;
export type RoadAiRunErrorCode = (typeof ROAD_AI_RUN_ERROR_CODES)[number];

/**
 * 非同期ジョブの識別子。v2-server が発行する不透明文字列で、client は中身を解釈しない。
 * ポーリング先の path パラメータに入るだけの値。
 */
export type RoadAiJobHandle = string & { readonly __brand: "RoadAiJobHandle" };

/**
 * ジョブ照会の結果。ポーリングの継続判定に使う。
 * pending / succeeded は照会レスポンス（200）由来、failed は HTTP エラー由来で、
 * roadAiAdapter が両者をこの 1 つの union に畳む。
 */
export type RoadAiJobProgress =
	| { status: "pending" } // 順番待ちまたは実行中。ポーリングを継続する
	| {
			status: "succeeded";
			candidateState: RoadAiCandidateState;
			diagnostics: readonly RoadAiDiagnostic[];
	  }
	| { status: "failed"; errorCode: RoadAiRunErrorCode };

/** ポーリング間隔（ミリ秒）。既存 roadAIUtils.js と同値（AC-31 により上限回数は設けない）。 */
export const ROAD_AI_POLL_INTERVAL_MS = 3_000 as const;

/** Max number of waypoints. Must match v2-server RoadAiSchema `waypoints.max(5)`. */
export const ROAD_AI_MAX_WAYPOINTS = 5;

/** Max number of points selectable in the point pick (start + waypoints + end). */
export const ROAD_AI_MAX_PICK_POINTS = ROAD_AI_MAX_WAYPOINTS + 2;

/** 実際に使用したアセット。登録時の紐付けに使う（AC-15）。 */
export interface RoadAiUsedSourceRef {
	pointCloudId: number;
	pointCloudAssetId: string;
	designAssetId: string | null;
}

/** LLM 応答 JSON のクライアント側契約。use_point_cloud_only が v1.2 の追加分（AC-35）。 */
export interface RoadAiChatbotResponse {
	response: string;
	objectives?: RoadAiObjective[];
	road_width_m?: number;
	max_slope_percent?: number;
	is_start_end_point?: boolean;
	is_create_road_ai?: boolean;
	use_point_cloud_only?: boolean;
}

/** v1.2 で addRoadTerrainByAi に追加する状態。 */
export interface RoadAiRunState {
	mode: RoadAiMode | null; // 検査で確定したモード（未判定は null）
	dataSelection: RoadAiDataSelection | null; // 使用データ確認メッセージの元データ
	pointCloudOnlyRequested: boolean; // LLM が抽出した点群のみモードの明示指定（AC-35）
	candidateState: RoadAiCandidateState; // 候補経路の正規化状態
	diagnostics: RoadAiDiagnostic[];
	usedSource: RoadAiUsedSourceRef | null; // 実行に実際に使われたアセット（AC-15）
	jobHandle: RoadAiJobHandle | null; // ポーリング中のジョブ。完了・失敗で null に戻す
	isRunning: boolean; // 実行中フラグ（既存）。受付からジョブ完了まで true（AC-41）
	runError: RoadAiRunErrorCode | null; // 実行中止の理由（AC-11）
}
